import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { captureMessage } from '@tink/core/src/sentry';
import { PaymentCheckType } from '../../DirectPayments/models/payment';
import { Api } from '../../../lib-api/Api';
import {
  CreateTransferRequest,
  Transfer,
  TransfersEntity,
  TransferStatus,
} from '../../DirectPayments/models/transfer';
import { setApplicationError } from '../../../stores/app-state-store';
import {
  getDuplicatePaymentFailure,
  getDuplicatePaymentSuccess,
} from '../../../stores/payment-store';
import { AppThunk } from '../../../stores/redux-store';
import {
  createTransferFailure,
  createTransferStart,
  createTransferSuccess,
  fetchTransferFailure,
  fetchTransferStart,
  fetchTransferSuccess,
} from '../../../stores/transfer-store';
import { getErrorMessageFromStatusCode } from '../../../utilities/error';
import { FRAUD_PREVENTION_CHECK_FAILED } from '../../components/FraudPreventionErrorScreen/FraudPreventionErrorScreen';
import { getPaymentPreInitiation, getTransferStatusForPaymentRequest } from './payments-services';

export const fetchTransferStatus =
  (transferId: string): AppThunk =>
  async (dispatch) => {
    dispatch(fetchTransferStart());

    const response = await Api.Transfer.getTransferStatus(transferId);

    if (response.isOk() && response.data) {
      dispatch(fetchTransferSuccess(response.data));
    } else {
      dispatch(fetchTransferFailure(getErrorMessageFromStatusCode(response.status)));
    }
  };

export const createTransfer =
  (transferRequest: CreateTransferRequest, config: ConfigurationModel): AppThunk =>
  async (dispatch) => {
    dispatch(createTransferStart());

    const {
      credentialsId,
      sourceUri,
      requestId,
      redirectUri,
      selectedProvider,
      selectedAuthenticationOptions,
    } = transferRequest;

    const paymentPreInitiation = await getPaymentPreInitiation(requestId, config);

    if (paymentPreInitiation) {
      const { isOk, data, status } = paymentPreInitiation;

      const { initiatedBefore, transferBasicInfo } = data || {};
      const initiatedBeforeType = initiatedBefore
        ? PaymentCheckType.INITIATED_BEFORE
        : PaymentCheckType.FAILED_OR_CANCELLED_BEFORE;

      if (status === 409) {
        dispatch(getDuplicatePaymentFailure(initiatedBeforeType));

        return;
      }

      if (!isOk() || !data) {
        dispatch(
          setApplicationError({
            errorStatus: ErrorStatus.INTERNAL_ERROR,
            errorReason: ErrorReason.REQUEST_FAILED_FETCH_PREINITIATION_CHECK,
          })
        );

        return;
      }

      if (
        transferBasicInfo &&
        transferBasicInfo.length > 0 &&
        config.featureFlags.includes(ConfigurationFeatureFlag.REJECT_MULTIPLE_TRANSFERS)
      ) {
        const existingTransfer = transferBasicInfo.find(
          (transfer) =>
            (transfer.status === TransferStatus.CREATED ||
              transfer.status === TransferStatus.AWAITING_CREDENTIALS) &&
            transfer.providerName === selectedProvider
        );

        if (existingTransfer) {
          dispatch(fetchTransferStart());

          const transferResponse = await Api.Transfer.getTransferStatus(
            existingTransfer.transferId
          );

          if (transferResponse.isOk() && transferResponse.data) {
            dispatch(fetchTransferSuccess(transferResponse.data));
          } else {
            dispatch(getDuplicatePaymentFailure(initiatedBeforeType));
          }
        } else {
          dispatch(getDuplicatePaymentFailure(initiatedBeforeType));
        }

        return;
      }

      dispatch(getDuplicatePaymentSuccess());
    }

    const response = await Api.Transfer.createTransfer(requestId, {
      credentialsId,
      sourceUri,
      redirectUri,
      selectedProvider,
      selectedAuthenticationOptions,
    });

    if (response.isOk() && response.data) {
      dispatch(createTransferSuccess(response.data));
    } else {
      if (response.status === 422) {
        dispatch(createTransferFailure(FRAUD_PREVENTION_CHECK_FAILED.errorReason));

        return;
      }

      /**
       * When we are here, the transfer creation either genuinely failed,
       * or the request failed because it was not gracefully ended, see PAYM-8957.
       * We have to check the status again, because it can very well have changed.
       */
      const transferStatusRequest = await getTransferStatusForPaymentRequest(requestId);

      if (transferStatusRequest?.isOk() && transferStatusRequest?.data) {
        dispatch(createTransferSuccess(transferStatusRequest.data));
      } else {
        if (response.status === 409) {
          dispatch(getDuplicatePaymentFailure(PaymentCheckType.INITIATED_BEFORE));

          return;
        }

        captureMessage('REQUEST_FAILED_CREATE_TRANSFER', {
          custom: {
            custom: {
              status: response.status,
              credentialsId,
              requestId,
              sourceUri,
              redirectUri,
              responseHeaders: JSON.stringify(response.headers),
              responseData: JSON.stringify(response.data),
            },
          },
        });

        dispatch(createTransferFailure(getErrorMessageFromStatusCode(response.status)));

        dispatch(
          setApplicationError({
            errorStatus: ErrorStatus.INTERNAL_ERROR,
            errorReason: ErrorReason.REQUEST_FAILED_CREATE_TRANSFER,
          })
        );
      }
    }
  };

export const pollTransferForSingleStatusChange =
  (
    transfer: TransfersEntity,
    TransferApi = Api.Transfer,
    timer: (callback: () => void, ms: number) => void = setTimeout
  ): AppThunk =>
  async (dispatch, getState) => {
    // Do not poll on complete transfer
    if (Transfer.isCompleted(transfer)) {
      return;
    }

    // Do not poll if transfer polling is already active
    if (getState().transfer.loading) {
      return;
    }

    dispatch(fetchTransferStart());

    const poll = async () => {
      const response = await TransferApi.getTransferStatus(transfer.id);

      if (response.isOk() && response.data) {
        if (response.data.status !== transfer.status) {
          dispatch(fetchTransferSuccess(response.data));

          return;
        }
        // No data received or Transfer Status is the same
        timer(poll, 1000);
      } else {
        dispatch(fetchTransferFailure(getErrorMessageFromStatusCode(response.status)));
      }
    };

    poll();
  };
